export enum EStorageNames {
  APPLICATIONS = 'applications',
  VISITATIONS = 'visitations',
  ANIMALS = 'animals',
  TENANTS = 'tenants',
  LANDLORDS = 'landlords',
  GO_TO_NEXT_VIDEO = 'goToNextVideo',
}

/**
 * Storage keys that are not per-list filter state. Kept out of EStorageNames because the filters
 * slice maps that enum over its state (`[key in EStorageNames]`), so a name added there becomes a
 * filter record the slice is then required to hold.
 */
export enum ECacheStorageNames {
  PROPERTY_LABELS = 'propertyLabels',
  // The admin the saved list filters belong to (PP-9563).
  FILTERS_OWNER = 'filtersOwner',
}

type TStorageKey = EStorageNames | ECacheStorageNames;

type TStorage = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getItem: (key: TStorageKey) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setItem: (key: TStorageKey, value: any) => void;
  removeItem: (key: TStorageKey) => void;
  clear: () => void;
};

const appSessionStorage: TStorage = {
  getItem: key => {
    // Guarded like the writes below: reading can throw on malformed stored JSON, and reading
    // storage at all throws when site data is blocked. Callers read during render, so an
    // unguarded throw here takes the page down instead of degrading (PP-10035).
    try {
      const item = sessionStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(error);
    }
  },
  removeItem: key => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error(error);
    }
  },
  clear: () => {
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error(error);
    }
  },
};

const appLocalStorage: TStorage = {
  getItem: key => {
    // See appSessionStorage.getItem - reads are guarded for the same reason.
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(error);
    }
  },
  removeItem: key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(error);
    }
  },
  clear: () => {
    try {
      localStorage.clear();
    } catch (error) {
      console.error(error);
    }
  },
};

// The saved list filters and pagination, which outlive sign-out for the admin who set them (PP-9563).
const SAVED_FILTER_KEYS: string[] = [
  EStorageNames.APPLICATIONS,
  EStorageNames.VISITATIONS,
  EStorageNames.ANIMALS,
  EStorageNames.TENANTS,
  EStorageNames.LANDLORDS,
];
const KEPT_KEYS = new Set<string>([
  ...SAVED_FILTER_KEYS,
  ECacheStorageNames.FILTERS_OWNER,
]);
// Cognito/Amplify session keys always go, whatever else they match.
const SESSION_PREFIXES = ['CognitoIdentityServiceProvider.', 'amplify-'];

const lastSignedInUser = (): string | null => {
  const key = Object.keys(localStorage).find(
    k =>
      k.startsWith('CognitoIdentityServiceProvider.') &&
      k.endsWith('.LastAuthUser'),
  );
  return key ? localStorage.getItem(key) : null;
};

/**
 * Clears the signed-in session from localStorage on sign-out, on auth failure and before taking over
 * a session handed in from another app: Cognito/Amplify tokens, the stored user and anything else not
 * kept. The saved list filters stay, marked with the admin they belong to, so only that admin gets
 * them back (see claimSavedFilters). It used to be `localStorage.clear()`, which also wiped every
 * saved filter (PP-9563). Keeping a short list rather than a list of session keys means a session key
 * added later is still cleared.
 */
const clearSessionStorage = () => {
  try {
    const owner = lastSignedInUser();
    Object.keys(localStorage)
      .filter(
        key =>
          SESSION_PREFIXES.some(prefix => key.startsWith(prefix)) ||
          !KEPT_KEYS.has(key),
      )
      .forEach(key => localStorage.removeItem(key));
    if (owner) {
      localStorage.setItem(ECacheStorageNames.FILTERS_OWNER, owner);
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * Called when an admin session starts. Saved filters belonging to anyone else, or to no recorded
 * admin, are removed, so one admin never opens the lists with another's filters and search text.
 * Returns true when it removed some, so the caller can reload the in-memory filters.
 */
const claimSavedFilters = (username: string | undefined): boolean => {
  try {
    const owner = localStorage.getItem(ECacheStorageNames.FILTERS_OWNER);
    const foreign =
      !username ||
      (owner !== username &&
        SAVED_FILTER_KEYS.some(key => localStorage.getItem(key) !== null));
    if (foreign) {
      SAVED_FILTER_KEYS.forEach(key => localStorage.removeItem(key));
    }
    if (username) {
      localStorage.setItem(ECacheStorageNames.FILTERS_OWNER, username);
    } else {
      localStorage.removeItem(ECacheStorageNames.FILTERS_OWNER);
    }
    return foreign;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export {
  appLocalStorage,
  appSessionStorage,
  clearSessionStorage,
  claimSavedFilters,
};
